import type { UseComboboxStateChangeTypes } from 'downshift';
import { useCombobox } from 'downshift';

import type { IOptions } from '../../../shared/utils/options';

import type { IInputDropdownProps } from '../_props';

/**
 * Typings
 */

type TUseStateEvent = {
  filterable?: boolean;
  isSelect?: boolean;
};

type THandleFocusEvent = {
  type: UseComboboxStateChangeTypes;
  inputValue: string;
  options: IOptions[];
  setListOptions: (listOptions: IOptions[]) => void;
};

type THandleBlurEvent = {
  type: UseComboboxStateChangeTypes;
  inputValue: string;
  toBeSelectedItem?: IOptions | null;
  setInputValue: (inputValue: string) => void;
  selectItem: (item: IOptions | null) => void;
} & Pick<IInputDropdownProps, 'onChangeText'>;

type TPropagateImmediateSearch = THandleBlurEvent;

/**
 * State Types Permissions
 */

const focusAllowedTypes = new Set<UseComboboxStateChangeTypes>([useCombobox.stateChangeTypes.InputFocus]);

const blurAllowedTypes = new Set<UseComboboxStateChangeTypes>([
  useCombobox.stateChangeTypes.InputBlur,
  useCombobox.stateChangeTypes.InputKeyDownEscape,
]);

const immediateSearchAllowedTypes = new Set<UseComboboxStateChangeTypes>([
  useCombobox.stateChangeTypes.InputKeyDownEnter,
]);

/**
 * Hook
 */

export const useStateEvent = ({ filterable, isSelect }: TUseStateEvent) => {
  /**
   * Handlers
   */

  const handleFocus = ({ type, inputValue, setListOptions, options }: THandleFocusEvent) => {
    if (!filterable || isSelect) return;

    if (focusAllowedTypes.has(type) && inputValue) {
      setListOptions(options.filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase())));
    }
  };

  const handleBlur = ({
    inputValue,
    setInputValue,
    selectItem,
    type,
    toBeSelectedItem,
    onChangeText,
  }: THandleBlurEvent) => {
    if (blurAllowedTypes.has(type)) {
      selectItem(toBeSelectedItem || null);

      setInputValue(inputValue);
      if (isSelect) return;

      if (!toBeSelectedItem) {
        onChangeText?.(inputValue, 'Search');
      }
    }
  };

  const propagateImmediateSearch = ({
    inputValue,
    setInputValue,
    type,
    toBeSelectedItem,
    onChangeText,
  }: TPropagateImmediateSearch) => {
    if (toBeSelectedItem) return;

    if (immediateSearchAllowedTypes.has(type)) {
      setInputValue(inputValue);

      onChangeText?.(inputValue, 'ImmediateSearch');
    }
  };

  /**
   * Return
   */

  return { handleFocus, handleBlur, propagateImmediateSearch };
};

export default useStateEvent;
